import { useEffect, useState } from "react";
import { FormEvent } from "react";
import { MdCancel } from "react-icons/md";
import common from "../utils/common";

import "bootstrap/dist/css/bootstrap.min.css";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  FloatingLabel,
} from "react-bootstrap";
import { AuthFunctions } from "../utils/FrontendAuthFunctions";
import { AxiosResponse } from "axios";
import { SystemMsg } from "../utils/SystemMsg";
import { useNavigate } from "react-router";
import toast, { Toaster } from "react-hot-toast";
import { BackendApi } from "../utils/BackendApi";
import { ApiRoute } from "../utils/ApiRoute";
import { useI18n } from "../i18n";

type PasswordFieldProp = {
  name: string;
  controlId: string;
  label: string;
  error: string;
  onChange: () => void;
};

function formString(data: FormData, key: string): string {
  const value = data.get(key);
  return typeof value === "string" ? value : "";
}

function resetFieldErrors(
  password: string,
  passwordConfirm: string,
): { password: string; confirm: string } {
  const passwordError = common.stringIsNullOrEmpty(password)
    ? SystemMsg.Reset.password_empty
    : "";
  let confirmError = "";
  if (common.stringIsNullOrEmpty(passwordConfirm)) {
    confirmError = SystemMsg.Reset.password_empty;
  } else if (passwordError.length === 0 && password !== passwordConfirm) {
    confirmError = SystemMsg.Reset.passwords_do_not_match;
  }
  return { password: passwordError, confirm: confirmError };
}

function PasswordField({
  name,
  controlId,
  label,
  error,
  onChange,
}: PasswordFieldProp) {
  return (
    <Form.Group>
      <FloatingLabel controlId={controlId} label={label}>
        <Form.Control
          name={name}
          type="password"
          placeholder={label}
          onChange={onChange}
          isInvalid={error.length > 0}
          required
        />
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
      </FloatingLabel>
    </Form.Group>
  );
}

export default function Reset() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const title = t("nav.resetPassword") + " - " + t("meta.appName");
  const [showControl, setShowControl] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>("");
  const [confirmError, setConfirmError] = useState<string>("");

  useEffect(() => {
    const onLoad = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const param = urlParams.get("param");
      if (common.stringIsNullOrEmpty(param)) {
        toast.error(SystemMsg.Reset.invalid_reset_link);
        return;
      }
      const onAuthSuccess = () => {
        setShowControl(true);
      };

      const onAuthFail = () => {
        setShowControl(false);
      };
      await AuthFunctions.reset(param ?? "", onAuthSuccess, onAuthFail);
    };
    onLoad();
  }, []);

  const clearFieldErrors = () => {
    setPasswordError("");
    setConfirmError("");
  };

  const onResetSuccessful = (res: AxiosResponse) => {
    toast.success(SystemMsg.Reset.password_reset_successful);
    clearFieldErrors();
    setTimeout(() => {
      navigate("/login");
    }, 2000);
  };

  const onResetFailed = (res: AxiosResponse | string) => {
    const response = res as AxiosResponse;
    if (response.status === 404) {
      setConfirmError(SystemMsg.Reset.user_not_found);
      return;
    }
    if (response.status === 410) {
      setConfirmError(SystemMsg.Reset.link_expired);
      return;
    }
    setConfirmError(SystemMsg.Reset.unable_to_reset);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const urlParams = new URLSearchParams(window.location.search);
    const param = urlParams.get("param") ?? "";
    if (common.stringIsNullOrEmpty(param)) {
      toast.error(SystemMsg.Reset.invalid_reset_link);
      return;
    }
    const data = new FormData(event.currentTarget);
    const password = formString(data, "password");
    const passwordConfirm = formString(data, "passwordConfirm");
    const errors = resetFieldErrors(password, passwordConfirm);

    setPasswordError(errors.password);
    setConfirmError(errors.confirm);
    if (errors.password || errors.confirm) {
      return;
    }

    await BackendApi.put(
      ApiRoute.reset,
      { password: password, param: param },
      onResetSuccessful,
      onResetFailed,
    );
  };

  return (
    <>
      {showControl && (
        <Container>
          <title>{title}</title>
          <Form onSubmit={onSubmit} noValidate>
            <Row className="mt-5"></Row>
            <Row className="mt-5"></Row>
            <Row className="mt-5"></Row>
            <Row className="mt-5"></Row>
            <Row className="justify-content-md-center">
              <Col md={5}>
                <h2 className="mb-3 text-center" style={{ userSelect: "none" }}>
                  <span>Reset Password</span>
                </h2>
                <PasswordField
                  name="password"
                  controlId="floatingPassword"
                  label="Password"
                  error={passwordError}
                  onChange={clearFieldErrors}
                />
                <div className="d-grid gap-2 mt-3">
                  <PasswordField
                    name="passwordConfirm"
                    controlId="floatingPasswordConfirm"
                    label="Confirm Password"
                    error={confirmError}
                    onChange={clearFieldErrors}
                  />
                </div>
                <div className="d-grid gap-2 mt-3">
                  <Button variant="secondary" size="lg" type="submit">
                    Submit
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
          <Toaster
            position="top-center"
            reverseOrder={false}
            toastOptions={{
              className: "",
              style: common.toasterStyle,
            }}
          />
        </Container>
      )}
      {!showControl && (
        <Container style={{ height: "100vh" }}>
          <Row
            className="justify-content-md-center"
            style={{
              position: "relative",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <Col md={6} className="text-center">
              <h2 className="mb-3 text-center" style={{ userSelect: "none" }}>
                <MdCancel style={{ fontSize: "40px" }} />
                <span style={{ marginLeft: "10px", marginTop: "2px" }}>
                  Link Expired
                </span>
              </h2>
            </Col>
          </Row>
        </Container>
      )}
    </>
  );
}
